// apps/web/src/pages/vendas/fechamento/rascunho-fiscal-revisao.tsx — Fase 12, Plano 14 (FECH-02, D-16..D-18, R12-06, T-12-79; UI-SPEC § Rascunho fiscal).
// 'Rascunho fiscal — revisão': 3 linhas por item (Peça, Tinta, Mão de obra), dados fiscais recolhidos, alertas (consumo estimado,
// mão de obra <= 0), pendências com 'Resolver' (modelo fiscal), 'Recalcular', 'Ajustar valores' e 'Aprovar rascunho'.
// Campo faltante aparece VAZIO com o badge de pendência — nunca um valor padrão (D-17). Sem segregação de funções no piloto:
// quem registrou o fechamento também pode aprovar; a tela mostra quem aprovou (auditado na API).
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, ClipboardList, Divide, FileClock, Loader2, RefreshCw, Scale } from 'lucide-react';
import type { LinhaRascunhoFiscal, RascunhoFiscalView } from '@pieltec/contracts';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { FormMessage } from '../../../components/ui/form-message';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  apresentacaoFonteConsumo,
  chaveEstadoFiscal,
  listaPendenciasRascunho,
  pendenciaResolvivelPorModeloFiscal,
  podeAjustarRascunho,
  podeAprovar,
  podeSelecionarModeloFiscal,
  rotuloEstadoRascunho,
  rotuloLinhaRascunho,
  valorOuVazio,
} from '../../../lib/vendas/fechamento';
import { formatarDataHora, formatarMoeda, formatarPercentual } from '../../../lib/vendas/formatacao';
import { ESTADO_FISCAL_CONFIG } from '../../pcp/retorno-panel';
import { AlertaConflitoPv } from '../detalhe/alerta-conflito-pv';
import { AprovarRascunhoDialog } from './aprovar-rascunho-dialog';
import { AjustarRascunhoDialog } from './ajustar-rascunho-dialog';
import { caminhoRascunho, escreverRascunho, MENSAGEM_FALHA_RECARGA_RASCUNHO } from './rascunho-api';
import { SelecionarModeloFiscalDialog } from './selecionar-modelo-fiscal-dialog';

interface RascunhoFiscalRevisaoProps {
  readonly retornoId: string;
  readonly onVoltar: () => void;
  /** Chamado depois de qualquer mudança gravada (recalcular, selecionar, ajustar, aprovar) para atualizar a lista. */
  readonly onAlterado?: () => void;
}

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const COPIA_MO_NAO_POSITIVA = 'Mão de obra zero ou negativa neste item (preço do serviço menor que o valor da tinta). A Omie recusa item de valor zero: a aprovação fica bloqueada até corrigir os valores.';
const ICONES_FONTE_CONSUMO = { Scale, Divide, ClipboardList, FileClock } as const;
const COPIA_CONSUMO_ESTIMADO = 'Sem consumo registrado no lote; usado o insumo previsto do roteiro × quantidade.';

function formatarDataHoraOuTraco(iso: string | null): string {
  return iso ? formatarDataHora(iso) : '—';
}

function BadgePendente() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
      <FileClock className="size-3 shrink-0" aria-hidden="true" />
      Pendente
    </span>
  );
}

/** Valor do rascunho: faltante = célula vazia + badge (nunca padrão); presente = formatado. */
function Valor({ valor, formatar }: { readonly valor: number | string | null | undefined; readonly formatar: (valor: number | string) => string }) {
  if (valorOuVazio(valor) === '') return <BadgePendente />;
  return <>{formatar(valor as number | string)}</>;
}

function DadosFiscais({ linha }: { readonly linha: LinhaRascunhoFiscal }) {
  const texto = (valor: string | number) => String(valor);
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground">Dados fiscais</summary>
      <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">CFOP</dt>
        <dd className="tabular-nums"><Valor valor={linha.cfop} formatar={texto} /></dd>
        <dt className="text-muted-foreground">NCM</dt>
        <dd className="tabular-nums"><Valor valor={linha.ncm} formatar={texto} /></dd>
        <dt className="text-muted-foreground">CST</dt>
        <dd className="tabular-nums"><Valor valor={linha.cst} formatar={texto} /></dd>
        <dt className="text-muted-foreground">cBenef</dt>
        <dd className="tabular-nums">{linha.cBenef ? linha.cBenef : '—'}</dd>
        <dt className="text-muted-foreground">ICMS %</dt>
        <dd className="tabular-nums"><Valor valor={linha.aliquotaIcms} formatar={(v) => formatarPercentual(Number(v))} /></dd>
      </dl>
    </details>
  );
}

export function RascunhoFiscalRevisao({ retornoId, onVoltar, onAlterado }: RascunhoFiscalRevisaoProps) {
  const [rascunho, setRascunho] = useState<RascunhoFiscalView | null>(null);
  const [erroCarga, setErroCarga] = useState(false);
  const [acao, setAcao] = useState<'recalcular' | 'aprovar' | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [conflito, setConflito] = useState<string | null>(null);
  const [recarregando, setRecarregando] = useState(false);
  const [ajustando, setAjustando] = useState(false);
  const [resolver, setResolver] = useState<{ itemRef: string; rotulo: string } | null>(null);
  const [confirmandoAprovar, setConfirmandoAprovar] = useState(false);

  /**
   * Lê o rascunho e diz se deu certo (WR-D1-14). Recarga `silenciosa` (botão "Recarregar" do conflito) que falha NÃO troca a tela
   * pelo cartão de erro: o rascunho antigo e os diálogos (justificativa/linhas digitadas) continuam, e quem chamou mostra o erro inline.
   */
  const carregar = useCallback(
    async (sinal: { vivo: boolean }, silenciosa = false): Promise<boolean> => {
      if (!silenciosa) setErroCarga(false);
      try {
        const res = await fetch(caminhoRascunho(retornoId), { credentials: 'include' });
        if (!sinal.vivo) return false;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const dados = (await res.json()) as RascunhoFiscalView;
        if (!sinal.vivo) return false;
        setRascunho(dados);
        return true;
      } catch {
        if (sinal.vivo && !silenciosa) setErroCarga(true);
        return false;
      }
    },
    [retornoId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  const aplicar = (atualizado: RascunhoFiscalView) => {
    setRascunho(atualizado);
    setErroAcao(null);
    setConflito(null);
    onAlterado?.();
  };

  const recarregar = async (): Promise<boolean> => {
    setRecarregando(true);
    try {
      const ok = await carregar({ vivo: true }, true);
      if (ok) {
        setConflito(null);
        setErroAcao(null);
      } else {
        // Mantém o conflito e o rascunho antigo na tela; os diálogos abertos preservam o que foi digitado.
        setErroAcao(MENSAGEM_FALHA_RECARGA_RASCUNHO);
      }
      return ok;
    } finally {
      setRecarregando(false);
    }
  };

  /**
   * POST em `/rascunho{sufixo}` com a versão vista pelo usuário: `/recalcular` e `/aprovar`
   * (POST /api/v1/vendas/fechamentos/:retornoId/rascunho/aprovar registra a intenção de faturamento; idempotente).
   */
  async function executar(nome: 'recalcular' | 'aprovar', sufixo: string): Promise<void> {
    if (!rascunho || acao) return;
    setAcao(nome);
    setErroAcao(null);
    setConflito(null);
    try {
      const resultado = await escreverRascunho(rascunho.retornoId, sufixo, { versao: rascunho.versao });
      if (resultado.tipo === 'ok') aplicar(resultado.view);
      else if (resultado.tipo === 'conflito') setConflito(resultado.message);
      else setErroAcao(resultado.message);
    } finally {
      setAcao(null);
      // A confirmação de aprovar fecha ao terminar (sucesso, conflito ou erro): o resultado aparece na tela.
      if (nome === 'aprovar') setConfirmandoAprovar(false);
    }
  }

  if (erroCarga) {
    return (
      <Card>
        <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-rose-700">Não foi possível carregar o rascunho fiscal. Verifique a conexão e tente novamente.</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onVoltar}>
              Voltar
            </Button>
            <Button type="button" variant="outline" onClick={() => void carregar({ vivo: true })}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (rascunho === null) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando rascunho fiscal">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const chaveFaturamento = chaveEstadoFiscal(rascunho.faturamentoEstado);
  const faturamento = chaveFaturamento ? ESTADO_FISCAL_CONFIG[chaveFaturamento] : null;
  const pendente = rascunho.estado === 'PENDENTE';
  const aprovado = rascunho.estado === 'APROVADO';
  const rotuloDoItem = (itemRef: string): string => {
    const item = rascunho.itens.find((candidato) => candidato.itemRef === itemRef);
    const peca = item?.linhas.find((linha) => linha.tipo === 'PECA');
    return peca?.numeroItemPv != null ? `item ${peca.numeroItemPv}` : 'item do fechamento';
  };
  const ocupado = acao !== null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={onVoltar}>
          <ArrowLeft aria-hidden="true" />
          Voltar
        </Button>
        <h2 className="text-base font-semibold">Rascunho fiscal — revisão</h2>
        <span className="text-sm text-muted-foreground tabular-nums">Retorno {retornoId.slice(0, 8)}</span>
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
            aprovado
              ? 'border-emerald-300 bg-emerald-100 text-emerald-800'
              : pendente
                ? 'border-amber-300 bg-amber-100 text-amber-800'
                : 'border-blue-300 bg-blue-100 text-blue-800'
          }`}
        >
          {rotuloEstadoRascunho(rascunho.estado)}
        </span>
        {faturamento && (
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${faturamento.corBadge}`}>{faturamento.label}</span>
        )}
      </div>

      {conflito && <AlertaConflitoPv mensagem={conflito} onRecarregar={() => void recarregar()} recarregando={recarregando} />}

      {aprovado && (
        <p className="flex items-center gap-2 text-sm font-medium text-emerald-800">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
          Aprovado por {rascunho.aprovadoPorNome ?? '—'} em {formatarDataHoraOuTraco(rascunho.aprovadoAt)}
        </p>
      )}

      {pendente && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900">
          <FileClock aria-hidden="true" />
          <AlertDescription className="text-amber-900">
            <p className="font-semibold">Rascunho pendente — falta: {listaPendenciasRascunho(rascunho.pendencias)}.</p>
            <p>Não é enviado à Omie até resolver.</p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {rascunho.pendencias.map((pendencia, indice) => (
                <li key={`${pendencia.codigo}:${pendencia.itemRef ?? ''}:${indice}`} className="flex flex-wrap items-center gap-2">
                  <span>{pendencia.detalhe}</span>
                  {pendencia.itemRef && pendenciaResolvivelPorModeloFiscal(pendencia.codigo) && podeSelecionarModeloFiscal(rascunho) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={ocupado}
                      onClick={() => setResolver({ itemRef: pendencia.itemRef as string, rotulo: rotuloDoItem(pendencia.itemRef as string) })}
                    >
                      Resolver
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {rascunho.itens.map((item) => {
        const numero = item.linhas[0]?.numeroItemPv;
        const alertaMo = rascunho.alertas.find((alerta) => alerta.itemRef === item.itemRef && alerta.codigo === 'MAO_DE_OBRA_NAO_POSITIVA');
        const alertaServico = rascunho.alertas.find((alerta) => alerta.itemRef === item.itemRef && alerta.codigo === 'SERVICO_DIVERGE_DO_PRECO');
        const fontes = item.linhas.map((linha) => apresentacaoFonteConsumo(linha.consumoFonte, linha.consumoEstimado));
        const temEstimado = fontes.some((fonte) => fonte?.estimado === true);
        const detalheMista = fontes.some((fonte) => fonte?.fonte === 'MISTA') ? apresentacaoFonteConsumo('MISTA', false)?.detalhe : undefined;
        const selecao = rascunho.selecoesModeloFiscal.find((candidata) => candidata.itemRef === item.itemRef);
        return (
          <Card key={item.itemRef} className="overflow-hidden py-0 gap-0">
            <div className="border-b bg-slate-50 px-3 py-2">
              <h3 className="text-sm font-semibold">{numero != null ? `Item ${numero}` : 'Item do fechamento'}</h3>
            </div>
            {alertaMo && (
              <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
                <AlertTriangle aria-hidden="true" />
                <AlertDescription>
                  <p className="font-semibold">{COPIA_MO_NAO_POSITIVA}</p>
                </AlertDescription>
              </Alert>
            )}
            {alertaServico && (
              <Alert className="rounded-none border-x-0 border-t-0 border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle aria-hidden="true" />
                <AlertDescription>
                  <p className="font-semibold">Serviço diverge do preço do pedido.</p>
                  <p>{alertaServico.detalhe}</p>
                </AlertDescription>
              </Alert>
            )}
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr>
                  <th scope="col" className={CABECALHO}>Linha</th>
                  <th scope="col" className={`${CABECALHO} text-right`}>Qtd</th>
                  <th scope="col" className={`${CABECALHO} text-right`}>Valor unit.</th>
                  <th scope="col" className={`${CABECALHO} text-right`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {item.linhas.map((linha, indice) => {
                  const moNaoPositiva = linha.tipo === 'MAO_DE_OBRA' && alertaMo !== undefined;
                  const fonteLinha = fontes[indice];
                  return (
                    <tr key={`${linha.tipo}:${linha.materialId ?? ''}:${indice}`} className="border-t align-top">
                      <td className="px-3 py-2">
                        <p className="flex flex-wrap items-center gap-2 font-medium">
                          {rotuloLinhaRascunho(linha.tipo)}
                          {fonteLinha && (
                            <span
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${fonteLinha.classe}`}
                              title={fonteLinha.detalhe}
                            >
                              {fonteLinha.icones.map((nome) => {
                                const Icone = ICONES_FONTE_CONSUMO[nome];
                                return <Icone key={nome} className="size-3" aria-hidden="true" />;
                              })}
                              {fonteLinha.rotulo}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{linha.descricao}</p>
                        <div className="mt-1">
                          <DadosFiscais linha={linha} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <Valor valor={linha.quantidade} formatar={(v) => valorOuVazio(v)} />
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${moNaoPositiva ? 'font-semibold text-rose-700' : ''}`}>
                        <Valor valor={linha.valorUnitario} formatar={(v) => formatarMoeda(Number(v), linha.tipo === 'TINTA' ? 4 : 2)} />
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${moNaoPositiva ? 'font-semibold text-rose-700' : ''}`}>
                        <Valor valor={linha.valorTotal} formatar={(v) => formatarMoeda(Number(v))} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(temEstimado || detalheMista || selecao) && (
              <div className="flex flex-col gap-1 border-t px-3 py-2 text-xs text-muted-foreground">
                {temEstimado && <p>{COPIA_CONSUMO_ESTIMADO}</p>}
                {detalheMista && <p>{detalheMista}</p>}
                {selecao && (
                  <p>
                    Modelo fiscal escolhido manualmente (versão {selecao.modeloFiscalVersaoId.slice(0, 8)}) por {selecao.selecionadoPorNome ?? '—'} em{' '}
                    {formatarDataHoraOuTraco(selecao.selecionadoAt)}: {selecao.justificativa}. A evidência do recebimento não foi alterada.
                  </p>
                )}
              </div>
            )}
          </Card>
        );
      })}

      <dl className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-muted-foreground">
        <div className="flex gap-1.5">
          <dt>Peças:</dt>
          <dd className="tabular-nums text-foreground">{formatarMoeda(rascunho.totais.valorPecas)}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt>Tintas:</dt>
          <dd className="tabular-nums text-foreground">{formatarMoeda(rascunho.totais.valorTintas)}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt>Mão de obra:</dt>
          <dd className="tabular-nums text-foreground">{formatarMoeda(rascunho.totais.valorMaoDeObra)}</dd>
        </div>
      </dl>

      {rascunho.ajusteJustificativa && (
        <p className="text-sm text-muted-foreground">
          Valores ajustados pelo revisor. Justificativa: <span className="text-foreground">{rascunho.ajusteJustificativa}</span>
        </p>
      )}

      {erroAcao && <FormMessage>{erroAcao}</FormMessage>}

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-2 rounded-lg border bg-white px-4 py-3 shadow-md">
        <Button type="button" variant="outline" onClick={onVoltar} disabled={ocupado}>
          Voltar
        </Button>
        {podeSelecionarModeloFiscal(rascunho) && (
          <Button type="button" variant="outline" onClick={() => void executar('recalcular', '/recalcular')} disabled={ocupado}>
            {acao === 'recalcular' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
            Recalcular
          </Button>
        )}
        {podeAjustarRascunho(rascunho) && (
          <Button type="button" variant="outline" onClick={() => setAjustando(true)} disabled={ocupado}>
            Ajustar valores
          </Button>
        )}
        {!aprovado && (
          <Button type="button" onClick={() => setConfirmandoAprovar(true)} disabled={!podeAprovar(rascunho) || ocupado}>
            Aprovar rascunho
          </Button>
        )}
      </div>

      <AprovarRascunhoDialog
        aberto={confirmandoAprovar}
        onAbertoChange={setConfirmandoAprovar}
        aprovando={acao === 'aprovar'}
        onConfirmar={() => void executar('aprovar', '/aprovar')}
      />
      <AjustarRascunhoDialog aberto={ajustando} onAbertoChange={setAjustando} rascunho={rascunho} onAjustado={aplicar} onRecarregar={recarregar} />
      {resolver && (
        <SelecionarModeloFiscalDialog
          aberto
          onAbertoChange={(aberto) => {
            if (!aberto) setResolver(null);
          }}
          retornoId={rascunho.retornoId}
          versaoRascunho={rascunho.versao}
          itemRef={resolver.itemRef}
          rotuloItem={resolver.rotulo}
          onSelecionado={aplicar}
          onRecarregar={recarregar}
        />
      )}
    </div>
  );
}
